import React from 'react'
import { Link } from 'react-router-dom'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HowToRegOutlinedIcon from '@material-ui/icons/HowToRegOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import PhonelinkEraseOutlinedIcon from '@material-ui/icons/PhonelinkEraseOutlined'
import useSmsPolicyStyles from './smsPolicyStyles'

const optInSteps = [
	'The authorized CCG user (including employees and contractors) opens the CCG SMS enrollment form.',
	'The user enters their mobile telephone number in the phone number field.',
	'The user checks an unchecked consent box containing the full consent language shown on this page. Consent is a separate, active step and is not bundled with any other agreement.',
	'After submitting the form, the user receives a one-time SMS confirming enrollment, with help and opt-out instructions.',
]

export default function SmsOptIn() {
	const classes = useSmsPolicyStyles()

	return (
		<div className={classes.root}>
			<div className={classes.effectiveDate}>
				<div className={classes.effectiveIcon} aria-hidden="true">
					<HowToRegOutlinedIcon />
				</div>
				<div>
					<p className={classes.effectiveTitle}>How Authorized CCG Users Opt In to CCG SMS Messages</p>
					<p className={classes.effectiveBody}>
						This public page is the hosted opt-in evidence for the Claim Consultant Group, LLC (“Claim Consultant Group,” “CCG”) SMS program. It shows who opts in, where consent is collected, the exact consent language, and a hosted screenshot of the enrollment form. End users are authorized CCG users (including employees and contractors). This is the only opt-in method used for this campaign.
					</p>
				</div>
			</div>

			<div className={classes.infoBanner} style={{ marginTop: 0, marginBottom: '2.75rem' }}>
				<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
				<span>Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. Mobile numbers are not shared with third parties or affiliates for marketing or promotional purposes.</span>
			</div>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<HowToRegOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>The Opt-In Process</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>
					End users opt in at <a href="https://www.claimconsultantgroup.com/sms-opt-in">https://www.claimconsultantgroup.com/sms-opt-in</a> by entering their mobile telephone number and checking an unchecked consent box. Enrollment is voluntary and is not a condition of employment, engagement, or receipt of related benefits. Consent is collected specifically for this SMS program and is not shared, transferred, or bundled with any other agreement.
				</p>
				<ul className={classes.checkList}>
					{optInSteps.map((item) => (
						<li key={item} className={classes.checkItem}>
							<CheckCircleIcon className={classes.checkIcon} aria-hidden="true" />
							<span>{item}</span>
						</li>
					))}
				</ul>
				<p className={classes.body}>
					Terms and Conditions: <Link to="/sms-terms">https://www.claimconsultantgroup.com/sms-terms</Link>. Privacy Policy: <Link to="/sms-privacy">https://www.claimconsultantgroup.com/sms-privacy</Link>.
				</p>
			</section>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<LockOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>Hosted Enrollment Form</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>
					The following public form is the consent experience presented to authorized CCG users (including employees and contractors). The consent checkbox is unchecked by default. Required disclosures appear next to the phone number field.
				</p>

				<img
					src="/images/sms-opt-in-form.png"
					alt="Hosted screenshot of the CCG SMS enrollment form showing the mobile number field, unchecked consent checkbox, message frequency, message and data rates disclosure, STOP and HELP instructions, and links to the SMS Terms and SMS Privacy Policy"
					className={classes.formShot}
				/>

				<form className={classes.formCard} onSubmit={(event) => event.preventDefault()} aria-label="CCG SMS enrollment form">
					<h3 className={classes.formTitle}>CCG SMS Enrollment</h3>
					<p className={classes.body}>Authorized CCG users (including employees and contractors)</p>
					<label className={classes.formLabel} htmlFor="ccg-sms-mobile">Mobile telephone number</label>
					<input
						id="ccg-sms-mobile"
						className={classes.formInput}
						type="tel"
						name="mobile"
						placeholder="(888) 000-0000"
						autoComplete="tel"
					/>
					<div className={classes.consentRow}>
						<input
							id="ccg-sms-consent"
							className={classes.consentBox}
							type="checkbox"
							name="smsConsent"
							defaultChecked={false}
						/>
						<label className={classes.consentText} htmlFor="ccg-sms-consent">
							I agree to receive non-marketing, work-related text messages from Claim Consultant Group, LLC at the mobile number provided, including one-time passcodes, security and account alerts, assignment notifications, work-status changes, and operational reminders. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. Consent is not a condition of employment, engagement, or receipt of related benefits. I have read the CCG <Link to="/sms-terms">SMS Terms and Conditions</Link> and <Link to="/sms-privacy">SMS Privacy Policy</Link>. Mobile numbers are not shared with third parties or affiliates for marketing or promotional purposes.
						</label>
					</div>
					<div className={classes.infoBanner} style={{ marginTop: '1.25rem' }}>
						<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
						<span>Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help.</span>
					</div>
					<button type="button" className={classes.formButton} disabled>Enroll in SMS</button>
				</form>
			</section>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<PhonelinkEraseOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>Enrollment Confirmation Message</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>
					After opting in, the user receives a one-time confirmation text message:
				</p>
				<div className={classes.infoBanner}>
					<span>
						“Claim Consultant Group: You are now enrolled in CCG work-related SMS notifications. Msg frequency varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to cancel.”
					</span>
				</div>
			</section>

			<section className={classes.legalSection}>
				<h2>Opting Out</h2>
				<p>Authorized CCG users (including employees and contractors) may opt out at any time by replying <span className={classes.keyword}>STOP</span> to any CCG SMS message. After the opt-out is processed, no further messages are sent unless the user affirmatively opts in again. Reply <span className={classes.keyword}>HELP</span> for assistance, or contact <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a> / <a href="tel:8886451480">(888) 645-1480</a>.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Privacy</h2>
				<p>We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties. See the <Link to="/sms-privacy">SMS Privacy Policy</Link> for details.</p>
			</section>
		</div>
	)
}
